import { enUS as datePickerEnUS, esES as datePickerEsES, ptBR as datePickerPtBR } from '@mui/x-date-pickers/locales';
import { enUS as dataGridEnUS, esES as dataGridEsES, ptBR as dataGridPtBR, ptPT as dataGridPtPT } from '@mui/x-data-grid/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/pt-br';
import { AppLocale, DEFAULT_LOCALE, isSupportedLocale } from './config';

export const APP_TO_INTL_LOCALE: Record<AppLocale, string> = {
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt-PT',
  'es-419': 'es-419',
  'en-US': 'en-US',
};

export const APP_TO_DAYJS_LOCALE: Record<AppLocale, string> = {
  'pt-BR': 'pt-br',
  'pt-PT': 'pt',
  'es-419': 'es',
  'en-US': 'en',
};

const DATE_PICKER_LOCALE_TEXT = {
  'pt-BR': datePickerPtBR.components.MuiLocalizationProvider.defaultProps.localeText,
  // The installed MUI X Date Pickers version does not expose a ptPT pack.
  'pt-PT': datePickerPtBR.components.MuiLocalizationProvider.defaultProps.localeText,
  'es-419': datePickerEsES.components.MuiLocalizationProvider.defaultProps.localeText,
  'en-US': datePickerEnUS.components.MuiLocalizationProvider.defaultProps.localeText,
} as const;

const DATA_GRID_LOCALE_TEXT = {
  'pt-BR': dataGridPtBR.components.MuiDataGrid.defaultProps.localeText,
  'pt-PT': dataGridPtPT.components.MuiDataGrid.defaultProps.localeText,
  'es-419': dataGridEsES.components.MuiDataGrid.defaultProps.localeText,
  'en-US': dataGridEnUS.components.MuiDataGrid.defaultProps.localeText,
} as const;

export const getAppLocale = (value: unknown): AppLocale =>
  isSupportedLocale(value) ? value : DEFAULT_LOCALE;

export const getIntlLocale = (value: unknown): string =>
  APP_TO_INTL_LOCALE[getAppLocale(value)];

export const getDayjsLocale = (value: unknown): string =>
  APP_TO_DAYJS_LOCALE[getAppLocale(value)];

export const syncDayjsLocale = (value: unknown): AppLocale => {
  const locale = getAppLocale(value);
  dayjs.locale(getDayjsLocale(locale));
  return locale;
};

export const getDatePickerLocaleText = (value: unknown) =>
  DATE_PICKER_LOCALE_TEXT[getAppLocale(value)];

export const getDataGridLocaleText = (value: unknown) =>
  DATA_GRID_LOCALE_TEXT[getAppLocale(value)];

export const formatDate = (
  value: string | number | Date | null | undefined,
  locale: unknown,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'numeric', year: 'numeric' },
): string => {
  if (value === null || value === undefined || value === '') return '';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(getIntlLocale(locale), options).format(date);
};

export const formatNumber = (
  value: number | null | undefined,
  locale: unknown,
  options?: Intl.NumberFormatOptions,
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';

  return new Intl.NumberFormat(getIntlLocale(locale), options).format(value);
};

